// react
import { useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Box, BoxProps, Divider, Paper, Skeleton, Stack, Typography } from '@mui/material';

// types
import { AiDocument, Condition, ContractV2, Invoice, Voucher } from '../../../../@types';

// ui
import { SectionHeader } from '../../../ui/SectionHeader/SectionHeader';

// child
import { isTerminal, Milestone } from '../Milestone/Milestone';

// props
export interface MilestonesProps {
	contract: ContractV2;
	currency: string;
	isLoading: boolean;
	milestones: Condition[];
	onChanged: (result?: { draft?: boolean }) => void;
	onError: (message?: string) => void;
	paidConditionIds: Set<number>;
	subtitle?: string;
	supplierEntityId: number | undefined;
	title: string;
	vouchers: Voucher[];
	invoices?: Invoice[];
	blobUrlByDocId?: Map<string, string>;
	documents?: AiDocument[];
}

const unlocksNextMilestone = (status: string | null | undefined): boolean =>
	isTerminal(status) || (status || '').toUpperCase() === 'SUBMITTED';

export const Milestones = ({
	contract,
	currency,
	isLoading,
	milestones,
	onChanged,
	onError,
	paidConditionIds,
	subtitle,
	supplierEntityId,
	title,
	vouchers,
	invoices,
	blobUrlByDocId,
	documents,
}: MilestonesProps) => {
	const { t } = useTranslation();

	const vouchersByCondition = useMemo(() => {
		const map = new Map<number, Voucher[]>();
		vouchers.forEach((v) => {
			if (v.condition != null) {
				const list = map.get(v.condition) || [];
				list.push(v);
				map.set(v.condition, list);
			}
		});
		return map;
	}, [vouchers]);

	// Sequencing gate — milestone N+1 is unlocked as soon as every earlier
	// milestone is either redeemed, in a terminal status, or already submitted
	// to the PIU. Submitting passes ownership off the supplier's plate, so
	// they shouldn't have to wait for VERIFY/REDEEM before starting the
	// next set of uploads.
	const gatedConditionIds = useMemo(() => {
		const gated = new Set<number>();
		for (let i = 0; i < milestones.length; i++) {
			const m = milestones[i];
			const earlier = milestones.filter((p) => p.sequenceOrder < m.sequenceOrder);
			const allComplete = earlier.every(
				(p) => unlocksNextMilestone(p.status) || paidConditionIds.has(p.id)
			);
			if (!allComplete) gated.add(m.id);
		}
		return gated;
	}, [milestones, paidConditionIds]);

	// Suppliers can only record milestone data once the PIU has activated the
	// contract. While DRAFT / SUBMITTED / CANCELLED / DEACTIVATED the form is
	// shown read-only with an explanatory banner. The server enforces the same
	// rule on writes; this gate just keeps the UI honest.
	const contractActive = (contract.status || '').toUpperCase() === 'ACTIVE';

	const BoxProps: BoxProps = {};

	const hasMilestones = milestones.length > 0;

	if (hasMilestones) {
		BoxProps.sx = {
			p: { xs: 2, sm: 3 },
		};
	}

	let content = null;

	if (isLoading) {
		content = <Skeleton height={80} variant="rounded" />;
	} else if (hasMilestones) {
		BoxProps.sx = {
			p: { xs: 2, sm: 3 },
		};

		if (contractActive) {
			content = (
				<Stack divider={<Divider sx={{ mx: { xs: -2, sm: -3 } }} />}>
					{milestones.map((m, idx) => (
						<Milestone
							key={m.id}
							contract={contract}
							currency={currency}
							index={idx}
							isGated={gatedConditionIds.has(m.id)}
							isPaid={paidConditionIds.has(m.id)}
							linkedVouchers={vouchersByCondition.get(m.id) || []}
							milestone={m}
							onChanged={onChanged}
							onError={onError}
							supplierEntityId={supplierEntityId}
							invoices={invoices}
							blobUrlByDocId={blobUrlByDocId}
							documents={documents}
						/>
					))}
				</Stack>
			);
		} else {
			content = <Alert severity="info">{t('milestoneEntryLockedUntilActive')}</Alert>;
		}
	} else {
		content = <Alert severity="info">{t('noPaymentMilestones')}</Alert>;
	}

	return (
		<Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
			<SectionHeader IconCircleProps={{ type: 'payment' }} title={title} />
			<Box {...BoxProps}>
				{subtitle && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{subtitle}
					</Typography>
				)}
				{content}
			</Box>
		</Paper>
	);
};
